import { useEffect, useRef, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio } from "@/audio/piano";
import { playRhythm, RhythmHandle } from "@/audio/rhythm";

/**
 * Piano Intermediate · Lesson 6 — Oom-Pah Rhythm Pattern, Piano Left Hand.
 *
 * The grooviest left-hand pattern on the piano. Jason's chart counts it in
 * eighth notes: the pinky plays the bass (LEGATO — "oom") on every beat and
 * the 3rd + 5th pop on every "and" (STACCATO — "pah!"). In the basic version
 * the pinky stays planted on the root. In the advanced version the pinky
 * floats between the root (beats 1, 3) and the lower fifth (beats 2, 4).
 * Default key is D major / D harmonic minor — same as Jason's demo.
 */

export default function PianoIntermediate6() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "6");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider
        defaultTonic="D"
        availableQualities={["major", "natural-minor", "harmonic-minor"]}
      >
        <PIShell
          title="Oom-Pah! Rhythm Pattern — Left Hand"
          exerciseGoal="The single most useful left-hand pattern in popular piano. Bass legato, chord staccato — and your right hand is finally free."
          bpm={108}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [chordIdx, setChordIdx] = useState(0); // 0=I, 1=IV, 2=V
  const [floating, setFloating] = useState(false);
  const [running, setRunning] = useState(false);
  const [beat, setBeat] = useState(-1);
  const handleRef = useRef<RhythmHandle | null>(null);

  // (Re)build the loop whenever any input changes while running.
  useEffect(() => {
    if (!running) return;
    let cancelled = false;
    (async () => {
      kickAudio();
      handleRef.current?.stop();
      const root = chordIdx === 0 ? 0 : chordIdx === 1 ? 5 : 7;
      const chordTones = triadTonesFor(k.quality, chordIdx);
      // One hand: pinky on the bass, middle finger and thumb on the 3rd and
      // 5th of the same chord just above it (Jason's chart: D, then F#-A).
      const bassRoot = tonicMidi - 12 + root;
      const pah = [chordTones[1], chordTones[2]].map((t) => midiToPitchName(bassRoot + t, k.useFlats));
      // 16 eighth-note steps per loop = 2 bars in 4/4, 2 steps per beat.
      // Jason's chart: "oom" on every beat (even steps), "pah" on every
      // "and" (odd steps). With `floating` on, the pinky plays the root on
      // beats 1 and 3 and the lower fifth (the chord's 5th, an octave down)
      // on beats 2 and 4.
      const rootName = midiToPitchName(bassRoot, k.useFlats);
      const lowFifthName = midiToPitchName(bassRoot - 5, k.useFlats);
      const events = Array.from({ length: 16 }, (_, step) =>
        step % 2 === 0
          ? {
              beat: step,
              notes: [floating && (step / 2) % 2 === 1 ? lowFifthName : rootName],
              durationSteps: 2,
              velocity: 0.85,
            }
          : { beat: step, notes: pah, durationSteps: 0.5, velocity: 0.7 }
      );
      const handle = await playRhythm(events, {
        bpm: 108,
        subdivide: 2,        // eighth-note steps
        totalSteps: 16,      // 2 bars × 8 eighth notes
        loop: true,
        click: false,        // bass note IS the metronome here; an extra click would muddy it
        beatsPerBar: 4,
        onStep: (s) => {
          if (cancelled) return;
          // Map step (0..15) onto the 8-slot "1 & 2 & 3 & 4 &" indicator
          setBeat(s % 8);
        },
      });
      if (!cancelled) handleRef.current = handle;
    })();
    return () => {
      cancelled = true;
      handleRef.current?.stop();
      handleRef.current = null;
    };
  }, [running, chordIdx, floating, k.tonicPc, k.quality, k.useFlats, tonicMidi]);

  // Master keyboard: lit current chord (root + 3rd or ♭3 + 5)
  const chordRootSemis = chordIdx === 0 ? 0 : chordIdx === 1 ? 5 : 7;
  const litHighlights: KeyHighlight[] = [
    { pc: (k.tonicPc + chordRootSemis) % 12, tone: "root", label: "BASS" },
    { pc: (k.tonicPc + chordRootSemis + triadTonesFor(k.quality, chordIdx)[1]) % 12, tone: "voice", label: "3rd" },
    { pc: (k.tonicPc + chordRootSemis + 7) % 12, tone: "voice", label: "5th" },
    ...(floating
      ? [{ pc: (k.tonicPc + chordRootSemis - 5 + 12) % 12, tone: "bass" as const, label: "lo-5" }]
      : []),
  ];

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={litHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel="The current chord — root, third, fifth"
        />
        <div className="mt-2 flex flex-wrap items-baseline gap-2">
          <span className="text-[12px] text-cyan-300/70">Chord on display:</span>
          {(["I", "IV", "V"] as const).map((label, i) => {
            const active = i === chordIdx;
            return (
              <button
                key={label}
                onClick={() => setChordIdx(i)}
                className={`min-w-[32px] rounded-md border px-2 py-1.5 font-mono text-[11px] font-bold ${
                  active
                    ? "border-amber-400/60 bg-amber-500/15 text-amber-200"
                    : "border-cyan-400/30 bg-cyan-500/5 text-cyan-200 hover:bg-cyan-500/15"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="The grooviest left-hand pattern on the piano">
          <p>
            <strong>Oom-pah</strong> is the bass-and-chord rhythmic split that
            works for Latin, salsa and just about any jumpy music. The left
            hand alternates between a
            <em> bass note</em> ("<strong>oom</strong>", legato — held) and
            a <em>chord</em> ("<strong>pah</strong>", staccato — bounced).
            It's the pattern your ear has heard ten thousand times without
            ever naming it.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "You can play Latin, can play like, you know, various forms of
            salsa music, you can play just any jumpy music. I call it the
            Umpah rhythm."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The articulation" title="Legato bass · staccato chord">
          <p>
            The articulation is everything. Get the bass note to <em>ring</em>
            (legato — let the previous bass note hold until the next one
            arrives) and the chord to <em>pop</em> (staccato — bounced off
            the keys). The contrast is what makes the pattern groove.
          </p>
          <p className="font-mono text-[14px] text-amber-200 my-2">
            1 oom · & pah · 2 oom · & pah · 3 oom · & pah · 4 oom · & pah
          </p>
          <p>
            Jason's chart counts it in eighth notes: the bass (oom) on every
            beat, the chord (pah) on every "and". The pah is just the 3rd and
            5th of the chord — your pinky already has the root.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <PINoteImage
          src="/notes/piano-intermediate/finals/06-oom-pah-rhythm-pattern-piano-left-hand-page1.png"
          caption="Jason's chart — the D major and D harmonic minor scales the chords come from."
        />
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Live cycle" title="Hear the pattern, switch chords on the fly">
          <p>
            Press start. The pattern loops. Use the <strong>I / IV / V</strong>{" "}
            buttons in the study deck above to flip the chord — your left
            hand has only three shapes to learn in any key.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => {
                if (running) {
                  setRunning(false);
                  setBeat(-1);
                } else {
                  setBeat(-1);
                  setRunning(true);
                }
              }}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300"
            >
              {running ? "■ Stop cycle" : "▶ Start the Oom-pah cycle"}
            </button>
            <button
              onClick={() => setFloating((f) => !f)}
              className={`rounded-lg border px-4 py-2 text-sm font-semibold ${
                floating
                  ? "border-rose-400/60 bg-rose-500/15 text-rose-200"
                  : "border-cyan-400/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20"
              }`}
            >
              {floating ? "● Floating fifth ON" : "○ Try the floating fifth"}
            </button>
          </div>
          <BeatIndicator beat={running ? beat : -1} />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="The advanced move" title="Float the pinky between root and lower fifth">
          <p>
            Once the basic Oom-pah is locked, you upgrade. The pinky stops
            staying on the root and starts <strong>floating</strong> between
            the root and the chord's fifth <em>below</em> the root. So the
            bass line walks: root, lower fifth, root, lower fifth — one note
            per beat, with the pah on every "and". The thumb and middle
            finger keep the 3rd and 5th; when the pinky slides down, the
            index finger takes over from the middle finger.
          </p>
          <p>
            Toggle the <strong>Floating fifth</strong> button above and let
            the cycle play — listen to the bass move on every beat.
          </p>
          <PIInsight>
            <p>
              The lower fifth is the chord's own 5th, dropped an octave so it
              sits <em>under</em> the root — a perfect fourth below it. On the{" "}
              <strong>{k.tonicName}</strong> chord, that's{" "}
              <strong>{k.nameForPc(k.pcFromDegree(5))}</strong> in a low
              octave swapping with{" "}
              <strong>{k.tonicName}</strong>. The ear hears the same chord;
              the bass line tells a longer story.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <PINoteImage
          src="/notes/piano-intermediate/finals/05-twinkle-twinkle-melodic-chord-inversions-page1.png"
          caption="Jason's chart — I, IV, I, V in D with root and lower fifth, and the oom-pah count."
        />
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Pick a quality" title="Major or minor — same pattern, different mood">
          <p>
            Use the <strong>Major / Natural Minor / Harmonic Minor</strong>{" "}
            buttons in the key picker to switch between sound-worlds. Each
            chord changes by at most one note; the bass line stays identical.
            Jason's major examples are tunes like The Lion Sleeps Tonight and
            Yankee Doodle. For something more Latin — sneaky and mischievous
            — he works in the harmonic minor: i, iv and a major V.
          </p>
        </Drill>
      </FadeIn>


      <FadeIn>
        <Drill number="6" label="Play along" title="Loop with right-hand chord-tone improv">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="6"
            label={`Oom-pah loop · ${k.tonicName}`}
            originalBpm={108}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Fifteen minutes, five rungs">
          <PracticeLadder
            intro={
              <>
                Start very slow. The articulation contrast (legato bass vs
                staccato chord) is the only thing that matters at first.
              </>
            }
            rungs={[
              { title: "Bass alone, legato", minutes: 2, accent: "amber",  detail: `Just the pinky on ${k.tonicName} — one note per beat, completely legato. Listen to the note ring out before the next attack.` },
              { title: "Chord alone, staccato", minutes: 2, accent: "cyan",   detail: "Just the chord — staccato bounce. No bass yet. Let your wrist do the bouncing, not your fingers." },
              { title: "Combine on I",         minutes: 3, accent: "rose",   detail: "Both, on the tonic chord only. Oom on every beat, pah on every 'and'. Don't change chord yet." },
              { title: "I → IV → V → I",       minutes: 4, accent: "emerald",detail: "Jason's chart: I, IV, I, V — then back. The bass moves with the chord; the articulation does not." },
              { title: "Floating fifth",       minutes: 4, accent: "violet", detail: "Now let the pinky float: root on beats 1 and 3, lower fifth on 2 and 4. Right hand free to steal chord tones for a melody." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── A small visual indicator for the live cycle ── */
function BeatIndicator({ beat }: { beat: number }) {
  return (
    <div className="mt-3 flex items-end gap-1.5 sm:gap-3">
      {[0, 1, 2, 3, 4, 5, 6, 7].map((b) => {
        const isOom = b % 2 === 0;
        const active = b === beat;
        const fill = isOom
          ? active ? "bg-amber-400" : "bg-amber-500/30"
          : active ? "bg-cyan-400" : "bg-cyan-500/30";
        return (
          <div key={b} className="flex flex-1 flex-col items-center">
            <motion.div
              animate={active ? { scale: 1.4 } : { scale: 1 }}
              transition={{ duration: 0.12 }}
              className={`h-7 w-7 rounded-full sm:h-10 sm:w-10 ${fill}`}
            />
            <div className="mt-1.5 text-center font-mono text-[10px] uppercase tracking-wider text-cyan-300/70">
              {isOom ? b / 2 + 1 : "&"}<br />{isOom ? "oom" : "pah"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The triad on I, IV or V for the current mode.
 *
 * Natural minor gives i, iv and v — all minor. HARMONIC minor raises the 7th,
 * which turns the v into a major V: that dominant is the entire reason the
 * scale exists, so playing it minor here would teach the opposite lesson.
 */
function triadTonesFor(quality: string, degreeIdx: number): number[] {
  const MAJOR = [0, 4, 7];
  const MINOR = [0, 3, 7];
  if (!isMinor(quality)) return MAJOR;
  const isV = degreeIdx === 2;
  if (isV && quality === "harmonic-minor") return MAJOR;
  return MINOR;
}

function isMinor(q: string) {
  return q !== "major";
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)] backdrop-blur-md">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard · live</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
